import React, { FunctionComponent, memo, useMemo } from "react";
import { Text, useColorModeValue } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { isEqual } from "lodash";

import { SiteConfig_siteConfig_dashboardConf_components } from "graphql/generated";

import StatsTable from "components/stats-table";
import BaseCard, { cardLabel, BaseCardProps } from "./BaseCard";
import RtuStatus from "../../command/RtuStatus"
import ControlMode, { CONTROL_MODE } from "../../command/ControlMode"


interface IProps extends BaseCardProps {
  baseUrl: string;
  data: any;
  config: SiteConfig_siteConfig_dashboardConf_components;
}


const RtuCommandCard: FunctionComponent<IProps> = memo(function RtuCommandCard({ data, config, baseUrl, ...rest }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const cardActive = useMemo(() => data?.control_mode > CONTROL_MODE.OFF, [data?.control_mode]);
  const controlModeSetpoint = useMemo(() => {
    switch (data?.control_mode) {
      case CONTROL_MODE.REACTIVE_POWER: return {
          label: intl.formatMessage({
            id: "dashboard__rtu_command__control_mode__setpoint",
            defaultMessage: "Setpoint",
          }),
          decimals: 0,
          unit: "kVAR",
          value: data?.reactive_power,
      }
      case CONTROL_MODE.POWER_FACTOR: return {
          label: intl.formatMessage({
            id: "dashboard__rtu_command__control_mode__setpoint",
            defaultMessage: "Setpoint",
          }),
          decimals: 2,
          value: data?.power_factor,
      }
      case CONTROL_MODE.VOLTAGE: return {
          label: intl.formatMessage({
            id: "dashboard__rtu_command__control_mode__setpoint",
            defaultMessage: "Setpoint",
          }),
          decimals: 0,
          unit: "V",
          value: data?.voltage,
      }
      default: return {
          label: intl.formatMessage({
            id: "dashboard__rtu_command__control_mode__no_setpoint",
            defaultMessage: "Setpoint",
          }),
          value: "-"
      }
    }
  }, [intl, data]);
  const darkIconUrl = useMemo(() => {
    switch (config.uiVersion) {
      case "opuncie": return "/images/svg/command-icon-cez-dark.svg";
    }
  }, [config.uiVersion]);
  const lightIconUrl = useMemo(() => {
    switch (config.uiVersion) {
      case "opuncie": return "/images/svg/command-icon-cez-light.svg";
    }
  }, [config.uiVersion]);
  const iconColorModeUrl = useColorModeValue(lightIconUrl, darkIconUrl);

  return (
    <BaseCard {...rest}
      title={intl.formatMessage({
        id: "dashboard__rtu_command____title",
        defaultMessage: `Distributor {label}`,
      }, {
        ...cardLabel(config),
      })}
      onClick={() => {
        navigate(`${baseUrl}/command`);
      }}
      highlighted={cardActive}
      icon={cardActive ? iconColorModeUrl : undefined}
    >
        <StatsTable
          inline
          rows={[
            {
              label: intl.formatMessage({
                id: "dashboard__rtu_command__rtu_state",
                defaultMessage: "RTU",
              }),
              value: <RtuStatus state={data?.state} />,
            },
            {
              label: intl.formatMessage({
                id: "dashboard__rtu_command__limits",
                defaultMessage: "Limits",
              }),
              unit: "kW",
              value: <>
                <Text title={intl.formatMessage({
                  id: "dashboard__rtu_command__limits__in",
                  defaultMessage: "Charging limit",
                })}>
                  {data?.limit_in}
                </Text>
                <Text px={2}>
                  |
                </Text>
                <Text title={intl.formatMessage({
                  id: "dashboard__rtu_command__limits__out",
                  defaultMessage: "Production limit",
                })}>
                  {data?.limit_out}
                </Text>
              </>,
            },
            {
              label: intl.formatMessage({
                id: "dashboard__rtu_command__control_mode",
                defaultMessage: "Control mode",
              }),
              value: <ControlMode state={data?.control_mode} />,
            },
            controlModeSetpoint,
          ]}
        />
    </BaseCard>
  );
}, isEqual);

export default RtuCommandCard;
